import { useState } from 'react';
import { cn } from './cn.js';

/**
 * Bloc de code avec bouton copier.
 *
 * Le retour de copie passe par le libellé du bouton et non par une couleur,
 * et l'annonce est faite en aria-live pour les lecteurs d'écran.
 */
export interface CodeBlockProps {
  code: string;
  language?: string;
  className?: string;
}

export function CodeBlock({ code, language = 'html', className }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard refusé (contexte non sécurisé, permission) : l'utilisateur
      // peut toujours sélectionner le texte à la main.
      setCopied(false);
    }
  }

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg border border-border bg-surface',
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <span className="font-mono text-xs uppercase tracking-wide text-text-subtle">
          {language}
        </span>
        <button
          type="button"
          onClick={copy}
          className="rounded-sm px-2 py-1 text-xs font-medium text-text-muted transition-colors hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {copied ? 'Copié' : 'Copier'}
        </button>
      </div>
      <pre className="overflow-x-auto p-4">
        <code className="font-mono text-sm leading-relaxed text-text">{code}</code>
      </pre>
      <span className="sr-only" aria-live="polite">
        {copied ? 'Code copié dans le presse-papiers' : ''}
      </span>
    </div>
  );
}
